<template>
  <div class="bg-white rounded-lg shadow-md">
    <!-- Header -->
    <div class="p-4 border-b border-secondary-100">
      <div class="flex items-center gap-2">
        <div class="w-8 h-8 bg-securdia-blue/10 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path>
          </svg>
        </div>
        <div>
          <h3 class="text-sm font-bold text-secondary-800">{{ $t('sales_dashboard.sales_trend_title') }}</h3>
          <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.sales_trend_subtitle') }}</p>
        </div>
      </div>
    </div>

    <!-- Chart content -->
    <div class="p-4">
      <div v-if="hasData" style="height: 300px;">
        <trend-line-chart 
          :chart-data="chartDatasets"
          :options="options"
          :height="300"
        />
      </div>
      
      <!-- Empty state -->
      <div v-else class="flex flex-col items-center justify-center py-12">
        <svg class="w-12 h-12 text-secondary-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
        </svg>
        <p class="text-secondary-500 text-sm">{{ $t('sales_dashboard.no_sales_data') }}</p>
        <p class="text-secondary-400 text-xs mt-1">{{ $t('sales_dashboard.loading_contracts') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';

// Create a reusable Line chart component
const TrendLineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      handler() {
        if (this.$data._chart) {
          this.$data._chart.destroy();
        }
        this.renderChart(this.chartData, this.options);
      },
      deep: true
    }
  }
};

export default {
  name: 'SalesTrendChart',
  components: {
    TrendLineChart
  },
  props: {
    contracts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {};
  },
  computed: {
    options() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
          labels: {
            fontFamily: "'Source Sans Pro', sans-serif",
            fontSize: 11,
            usePointStyle: true,
            padding: 15
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleFontColor: '#374151',
          bodyFontColor: '#6B7280',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          titleFontFamily: "'Source Sans Pro', sans-serif",
          bodyFontFamily: "'Source Sans Pro', sans-serif",
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.yLabel;
              if (label === self.$t('sales_dashboard.contracts_label')) {
                return `${label}: ${value} ${self.$t('sales_dashboard.unit_pcs')}`;
              }
              return `${label}: ${self.formatCurrency(value)}`;
            }
          }
        },
        hover: {
          mode: 'index',
          intersect: false
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontFamily: "'Source Sans Pro', sans-serif",
              fontSize: 10,
              maxRotation: 45,
              minRotation: 45
            }
          }],
          yAxes: [
            {
              id: 'y-axis-contracts',
              type: 'linear',
              position: 'left',
              gridLines: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                fontFamily: "'Source Sans Pro', sans-serif",
                fontSize: 10,
                beginAtZero: true
              },
              scaleLabel: {
                display: true,
                labelString: this.$t('sales_dashboard.contracts_axis'),
                fontFamily: "'Source Sans Pro', sans-serif",
                fontSize: 10
              }
            },
            {
              id: 'y-axis-revenue',
              type: 'linear',
              position: 'right',
              gridLines: {
                drawOnChartArea: false
              },
              ticks: {
                fontFamily: "'Source Sans Pro', sans-serif",
                fontSize: 10,
                beginAtZero: true,
                callback: (value) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: this.$t('sales_dashboard.currency_axis'),
                fontFamily: "'Source Sans Pro', sans-serif",
                fontSize: 10
              }
            }
          ]
        }
      };
    },
    hasData() {
      return this.contracts.length > 0 && this.processedData.labels.length > 0;
    },
    processedData() {
      // Group contracts by year-month
      const monthlyData = {};
      
      this.contracts.forEach(contract => {
        const date = new Date(contract.startDate || contract.contract_start || contract.created_at);
        if (isNaN(date.getTime())) return;
        
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[key]) {
          monthlyData[key] = {
            contracts: 0,
            cash: 0,
            mrr: 0
          };
        }
        
        // Check for rental/subscription contracts (agreement_category values)
        const isRental = ['rental', 'financed', 'subscription'].includes(contract.category);
        if (isRental) {
          monthlyData[key].contracts++;
          monthlyData[key].mrr += contract.monthlyValue || contract.mrr || 0;
        } else {
          monthlyData[key].cash += contract.cashValue || contract.total_value || 0;
        }
      });
      
      // Sort by date and get last 24 months
      const sortedKeys = Object.keys(monthlyData).sort().slice(-24);
      
      return {
        labels: sortedKeys.map(k => {
          const [year, month] = k.split('-');
          return `${this.getMonthName(parseInt(month))} ${year.slice(2)}`;
        }),
        contracts: sortedKeys.map(k => monthlyData[k].contracts),
        cash: sortedKeys.map(k => monthlyData[k].cash),
        mrr: sortedKeys.map(k => monthlyData[k].mrr)
      };
    },
    chartDatasets() {
      const data = this.processedData;
      return {
        labels: data.labels,
        datasets: [
          {
            label: this.$t('sales_dashboard.contracts_label'),
            data: data.contracts,
            borderColor: '#1A8DE9',
            backgroundColor: 'rgba(26, 141, 233, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            yAxisID: 'y-axis-contracts',
            lineTension: 0.3
          },
          {
            label: this.$t('sales_dashboard.cash_kr_label'),
            data: data.cash,
            borderColor: '#F29100',
            backgroundColor: 'rgba(242, 145, 0, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            yAxisID: 'y-axis-revenue',
            lineTension: 0.3
          },
          {
            label: this.$t('sales_dashboard.mrr_kr_label'),
            data: data.mrr,
            borderColor: '#22C55E',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            yAxisID: 'y-axis-revenue',
            lineTension: 0.3
          }
        ]
      };
    }
  },
  methods: {
    getMonthName(month) {
      const monthKeys = [
        'sales_dashboard.month_jan', 'sales_dashboard.month_feb', 'sales_dashboard.month_mar',
        'sales_dashboard.month_apr', 'sales_dashboard.month_may', 'sales_dashboard.month_jun',
        'sales_dashboard.month_jul', 'sales_dashboard.month_aug', 'sales_dashboard.month_sep',
        'sales_dashboard.month_oct', 'sales_dashboard.month_nov', 'sales_dashboard.month_dec'
      ];
      return this.$t(monthKeys[month - 1]) || '';
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>
